import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'

import { useTranslation } from 'next-i18next'
import React from "react";

type ChangeLogElementProps = {
  value: any;
  userData: any;
  compact?: boolean;
  withAccordion?: boolean;
};

const ChangeLogElement = ({
  value,
  userData,
  compact = false,
  withAccordion = true,
}: ChangeLogElementProps) => {
  const {t} = useTranslation('common')

  const badgeProps = {
    variant: "subtle" as const,
    colorScheme: "gray" as const,
    fontSize: compact ? "2xs" : "xs",
    px: compact ? 1.5 : 2,
    py: 0.5,
    borderRadius: "md",
    lineHeight: "short" as const,
  }

  const labelFontSize = compact ? "sm" : "md"
  const metaFontSize = compact ? "xs" : "sm"
  const cardPx = compact ? 3 : 4
  const cardPy = compact ? 2 : 3

  const content = (
    <Box pt={withAccordion ? 0 : (compact ? 0 : 4)}>
      {Array.isArray(value) && value.length > 0 && (
        <VStack align="stretch" spacing={compact ? 2 : 3}>
          {value.sort((a, b) => b.date - a.date).map((changelogElement) => {
            const userName = userData?.find?.((obj) => obj?._id === changelogElement.user)?.username
            return (
              <Box
                key={changelogElement._id + "-change"}
                borderWidth={1}
                borderRadius="md"
                mt={0}
                px={cardPx}
                py={cardPy}
              >
                <VStack align="stretch" spacing={compact ? 1 : 1.5}>
                  {changelogElement.updates.map((singleUpdate) => {
                    const isCreated = singleUpdate.field == "created"
                    return (
                      <Flex
                        key={singleUpdate._id + "update"}
                        wrap="wrap"
                        align="center"
                        rowGap={1}
                        columnGap={2}
                      >
                        {isCreated ? (
                          <>
                            <Badge {...badgeProps}>{String(singleUpdate.field).toUpperCase()}</Badge>
                            <Text fontSize={labelFontSize}>{t("changeLogelement.thisInvoice")}</Text>
                          </>
                        ) : (
                          <>
                            <Text fontSize={labelFontSize}>{t("changeLogelement.changed")}</Text>
                            <Badge {...badgeProps}>{singleUpdate.field}</Badge>
                            <Text fontSize={labelFontSize}>{t("changeLogelement.from")}</Text>
                            <Badge {...badgeProps} maxW="100%" overflow="hidden" textOverflow="ellipsis">
                              {String(singleUpdate.oldValue ?? "")}
                            </Badge>
                            <Text fontSize={labelFontSize}>{t("changeLogelement.to")}</Text>
                            <Badge {...badgeProps} maxW="100%" overflow="hidden" textOverflow="ellipsis">
                              {String(singleUpdate.newValue ?? "")}
                            </Badge>
                          </>
                        )}
                      </Flex>
                    )
                  })}

                  <HStack spacing={2} wrap="wrap">
                    {userName ? (
                      <Text fontWeight="600" fontSize={metaFontSize}>
                        {userName}
                      </Text>
                    ) : null}
                    <Text fontSize={metaFontSize} color="gray.500">
                      {new Date(changelogElement.date).toLocaleDateString()}{" "}
                      {new Date(changelogElement.date).toLocaleTimeString()}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );

  if (!withAccordion) {
    return content;
  }

  return (
    <Accordion
      mt={compact ? 0 : "8"}
      pb={compact ? 0 : 10}
      borderBottom={"0px solid white"}
      allowToggle
    >
      <AccordionItem>
        <AccordionButton
          className={"without-highligt"}
          pt={compact ? 2 : 4}
          pb={compact ? 2 : 4}
          paddingLeft={0}
        >
          <Heading as="h3" size={compact ? "sm" : "md"}>
            {t("changeLogelement.changeHistory")}
          </Heading>
          <Spacer />
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={compact ? 2 : 4} pl={0} pr={0}>
          {content}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default ChangeLogElement;